/**
 * Estado de filtrado de alto nivel, compartido por el informe y la tabla.
 *
 *   { search, quick: { campo: [valores] }, advanced: { logic, conditions } }
 *
 * Todo es serializable, por lo que puede guardarse como vista favorita
 * o volcarse a la URL sin transformaciones adicionales.
 */

import { resolveField } from './fieldsNew'
import {
  applyFilterGroups,
  applyQuickSearch,
  countActiveConditions,
  isConditionActive,
} from './filtersNew'

export const QUICK_FILTER_FIELDS = ['proyecto', 'transportista', 'gestor', 'tipoResiduo', 'tipoObra', 'codZona']

export function createFilterState(quickFields = QUICK_FILTER_FIELDS) {
  return {
    search: '',
    quick: quickFields.reduce((acc, key) => {
      acc[key] = []
      return acc
    }, {}),
    advanced: { logic: 'AND', conditions: [] },
  }
}

/**
 * Convierte los selectores rápidos en un grupo de condiciones AND.
 * Los campos tipo dateRange (un solo control con fecha inicio y fin) filtran
 * por la fecha final del registro: la FechaFin del ítem debe caer dentro del
 * rango seleccionado (equivalente a SQL `FechaFin BETWEEN inicio AND fin`).
 */
export function quickToGroup(quick, fields) {
  const conditions = []
  Object.entries(quick || {}).forEach(([field, values]) => {
    if (!Array.isArray(values)) return
    const meta = resolveField(field, fields)
    if (meta?.type === 'dateRange') {
      const [start, end] = values
      const target = meta.rangeEndField || field
      if (start) {
        conditions.push({
          id: `quick-${field}-from`,
          field: target,
          operator: 'dateFrom',
          value: start,
          value2: '',
        })
      }
      if (end) {
        conditions.push({
          id: `quick-${field}-to`,
          field: target,
          operator: 'dateTo',
          value: end,
          value2: '',
        })
      }
      return
    }
    if (values.length > 0) {
      conditions.push({
        id: `quick-${field}`,
        field,
        operator: 'in',
        value: values,
        value2: '',
      })
    }
  })
  return { logic: 'AND', conditions }
}

/** Campos sobre los que actúa la búsqueda libre. */
export function searchableKeys(fields) {
  return fields
    .filter((field) => !field.advanced && ['text', 'enum'].includes(field.type))
    .map((field) => field.key)
}

/**
 * Aplica el estado completo (búsqueda + rápidos + avanzados + grupos extra).
 * Los grupos extra permiten sumar los filtros por columna de la tabla o los
 * filtros propios de un widget sin duplicar lógica.
 */
export function applyFilterState(rows, state, fields, extraGroups = []) {
  const searched = applyQuickSearch(
    rows,
    state?.search || '',
    searchableKeys(fields),
    fields
  )
  return applyFilterGroups(
    searched,
    [quickToGroup(state?.quick, fields), state?.advanced, ...extraGroups],
    fields
  )
}

export function activeFilterCount(state) {
  const quick = Object.values(state?.quick || {}).filter((values) => values.length > 0).length
  return quick + countActiveConditions(state?.advanced) + (state?.search ? 1 : 0)
}

/** Chips de filtros activos (para poder quitarlos uno a uno). */
export function activeFilterChips(state, fields) {
  const chips = []
  if (state?.search) {
    chips.push({ id: 'search', kind: 'search', label: `Búsqueda: "${state.search}"` })
  }
  Object.entries(state?.quick || {}).forEach(([field, values]) => {
    if (!values || !values.length) return
    const meta = resolveField(field, fields)
    let detail
    if (meta?.type === 'dateRange') {
      detail = `${values[0] || '…'} – ${values[1] || '…'}`
    } else {
      detail =
        values.length > 2 ? `${values.length} valores` : values.join(', ')
    }
    chips.push({
      id: `quick-${field}`,
      kind: 'quick',
      field,
      label: `${meta?.label || field}: ${detail}`,
    })
  })
  ;(state?.advanced?.conditions || [])
    .filter((condition) => isConditionActive(condition, fields))
    .forEach((condition) => {
      chips.push({ id: condition.id, kind: 'advanced', conditionId: condition.id })
    })
  return chips
}
